import { jsPDF } from 'jspdf';

export interface VotacaoEtapa1ExportItem {
  titulo: string;
  descricao: string;
  resultado: string;
  videoUrl?: string;
  /** Categoria de prêmio da iniciativa (label exibido). Quando informado, aparece no cabeçalho do card no PDF. */
  categoria?: string;
}

const CSV_HEADERS = ['Título', 'Descrição', 'Resultado', 'Link do vídeo'];

function sanitizeFilename(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

function escapeCsvValue(value: string): string {
  const normalized = value.replace(/\r?\n|\r/g, ' ').trim();
  const escaped = normalized.replace(/"/g, '""');
  return `"${escaped}"`;
}

function isHttpUrl(value?: string): boolean {
  if (!value) return false;
  return /^https?:\/\//i.test(value.trim());
}

function buildCsvVideoCell(videoUrl?: string): string {
  if (!isHttpUrl(videoUrl)) {
    return escapeCsvValue(videoUrl || '');
  }

  return escapeCsvValue(String(videoUrl || '').trim());
}

/**
 * Sanitiza texto para a fonte Helvetica padrão do jsPDF, que só renderiza
 * CP1252. Sem isto, caracteres fora do range (ex.: aspas curvas, en/em dash,
 * espaço não separável, NBSP, ellipsis, alguns símbolos) podem corromper a
 * codificação e gerar saídas como "&f&i&n&a..." no PDF.
 */
function sanitizeForPdf(text: string): string {
  if (!text) return '';
  return text
    .normalize('NFC')
    // Tags HTML inseridas pelo backend (ex.: <br>, <br/>, <p>) viram espaços;
    // o split colapsa a sequência de espaços e o texto segue corrido.
    .replace(/<\s*br\s*\/?\s*>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    // Entidades HTML comuns
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/[‐‑‒–—―]/g, '-')
    .replace(/…/g, '...')
    .replace(/ /g, ' ')
    .replace(/[​-‍﻿]/g, '')
    .replace(/[^\x00-\xFF]/g, (char) => {
      // Tenta achatar para ASCII via decomposição (remove acentos combinantes).
      const ascii = char.normalize('NFD').replace(/[̀-ͯ]/g, '');
      return /^[\x00-\xFF]+$/.test(ascii) ? ascii : '';
    });
}

function truncateTextForWidth(doc: jsPDF, text: string, maxWidth: number): string {
  const normalized = (text || '-').replace(/\s+/g, ' ').trim() || '-';
  if (doc.getTextWidth(normalized) <= maxWidth) {
    return normalized;
  }

  const ellipsis = '...';
  let truncated = normalized;

  while (truncated.length > 0 && doc.getTextWidth(`${truncated}${ellipsis}`) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }

  return `${truncated}${ellipsis}`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function exportarIniciativasCsv(
  iniciativas: VotacaoEtapa1ExportItem[],
  baseFilename = 'iniciativas-selecionadas'
): void {
  const rows = iniciativas.map((item) => {
    return [
      escapeCsvValue(item.titulo),
      escapeCsvValue(item.descricao),
      escapeCsvValue(item.resultado),
      buildCsvVideoCell(item.videoUrl)
    ].join(';');
  });

  const csvContent = [CSV_HEADERS.join(';'), ...rows].join('\n');
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const filename = `${sanitizeFilename(baseFilename) || 'iniciativas-selecionadas'}.csv`;

  triggerDownload(blob, filename);
}

export function exportarIniciativasPdf(
  iniciativas: VotacaoEtapa1ExportItem[],
  baseFilename = 'iniciativas-selecionadas',
  titulo = 'Lista de iniciativas finalistas Classificadas na Etapa 2'
): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 36;
  const contentWidth = pageWidth - marginX * 2;
  const cardPadding = 14;
  const sectionGap = 10;
  const lineHeight = 13;

  let cursorY = 42;

  const ensurePageSpace = (requiredHeight: number) => {
    if (cursorY + requiredHeight > pageHeight - 36) {
      doc.addPage();
      cursorY = 42;
    }
  };

  const split = (text: string, width: number) => {
    const normalized = (text || '-').replace(/\s+/g, ' ').trim();
    return doc.splitTextToSize(normalized, width) as string[];
  };

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(sanitizeForPdf(titulo), marginX, cursorY);
  cursorY += 22;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')}`, marginX, cursorY);
  cursorY += 18;

  // Sanitiza todos os campos textuais antes da renderização para evitar
  // glyphs fora do CP1252 (problema do "&f&i&n&a..." no PDF).
  const itensSanitizados = iniciativas.map((item) => ({
    titulo: sanitizeForPdf(item.titulo),
    descricao: sanitizeForPdf(item.descricao),
    resultado: sanitizeForPdf(item.resultado),
    categoria: item.categoria ? sanitizeForPdf(item.categoria) : '',
    videoUrl: item.videoUrl,
  }));

  // Agrupa por categoria (sort estável preserva a ordem original dentro de
  // cada categoria) e pré-calcula o total para o label "X de Y".
  const totaisPorCategoria = new Map<string, number>();
  itensSanitizados.forEach((item) => {
    const chave = item.categoria || 'Sem categoria';
    totaisPorCategoria.set(chave, (totaisPorCategoria.get(chave) || 0) + 1);
  });

  const itensOrdenados = [...itensSanitizados].sort((a, b) =>
    (a.categoria || '').localeCompare(b.categoria || '', 'pt-BR')
  );

  const contadorPorCategoria = new Map<string, number>();

  itensOrdenados.forEach((item) => {
    const chaveCategoria = item.categoria || 'Sem categoria';
    const indiceCategoria = (contadorPorCategoria.get(chaveCategoria) || 0) + 1;
    contadorPorCategoria.set(chaveCategoria, indiceCategoria);
    const totalCategoria = totaisPorCategoria.get(chaveCategoria) || 0;
    const cardInnerWidth = contentWidth - cardPadding * 2;

    // CRÍTICO: `splitTextToSize` mede a largura usando a fonte/tamanho ATUAL
    // do doc. É necessário aplicar o font/size de cada seção ANTES de chamar
    // o split, senão o título (size 12 bold) é quebrado com base nas métricas
    // da última seção renderizada (size 10) e estoura a borda do card.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    const titleLines = split(item.titulo, cardInnerWidth);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const descricaoLines = split(item.descricao, cardInnerWidth);
    const resultadoLines = split(item.resultado, cardInnerWidth);
    const hasVideoUrl = isHttpUrl(item.videoUrl);
    const videoText = hasVideoUrl
      ? truncateTextForWidth(doc, item.videoUrl || '', cardInnerWidth)
      : '-';

    const titleHeight = titleLines.length * 15;
    const sectionLabelHeight = 12;
    const descricaoHeight = descricaoLines.length * lineHeight;
    const resultadoHeight = resultadoLines.length * lineHeight;
    const videoHeight = lineHeight;

    const cardHeight =
      cardPadding +
      18 +
      titleHeight +
      sectionGap +
      sectionLabelHeight +
      descricaoHeight +
      sectionGap +
      sectionLabelHeight +
      resultadoHeight +
      sectionGap +
      sectionLabelHeight +
      videoHeight +
      cardPadding;

    ensurePageSpace(cardHeight + 12);

    const cardX = marginX;
    const cardY = cursorY;
    const headerHeight = 24;

    doc.setDrawColor(51, 84, 253);
    doc.setLineWidth(1);
    doc.roundedRect(cardX, cardY, contentWidth, cardHeight, 8, 8, 'S');

    doc.setFillColor(245, 247, 255);
    doc.roundedRect(cardX, cardY, contentWidth, headerHeight, 8, 8, 'F');
    doc.setDrawColor(229, 234, 255);
    doc.line(cardX, cardY + headerHeight, cardX + contentWidth, cardY + headerHeight);

    let textY = cardY + 16;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(51, 84, 253);
    const categoriaLabel = item.categoria || 'Sem categoria';
    const categoriaTexto = truncateTextForWidth(
      doc,
      categoriaLabel,
      contentWidth / 2 - cardPadding
    );
    doc.text(categoriaTexto, cardX + cardPadding, textY);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(51, 84, 253);
    doc.text(
      `${indiceCategoria} de ${totalCategoria}`,
      cardX + contentWidth - cardPadding,
      textY,
      { align: 'right' }
    );

    textY = cardY + headerHeight + cardPadding;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(17, 18, 20);
    titleLines.forEach((line) => {
      doc.text(line, cardX + cardPadding, textY);
      textY += 15;
    });

    textY += sectionGap - 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(75, 84, 99);
    doc.text('Descrição', cardX + cardPadding, textY);

    textY += sectionLabelHeight;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(31, 41, 55);
    descricaoLines.forEach((line) => {
      doc.text(line, cardX + cardPadding, textY);
      textY += lineHeight;
    });

    textY += sectionGap - 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(75, 84, 99);
    doc.text('Resultado', cardX + cardPadding, textY);

    textY += sectionLabelHeight;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(31, 41, 55);
    resultadoLines.forEach((line) => {
      doc.text(line, cardX + cardPadding, textY);
      textY += lineHeight;
    });

    textY += sectionGap - 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(75, 84, 99);
    doc.text('Link do vídeo', cardX + cardPadding, textY);

    textY += sectionLabelHeight;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(51, 84, 253);
    const videoX = cardX + cardPadding;
    if (hasVideoUrl) {
      doc.textWithLink(videoText, videoX, textY, { url: item.videoUrl as string });
    } else {
      doc.text(videoText, videoX, textY);
    }
    textY += lineHeight;

    doc.setTextColor(0, 0, 0);
    cursorY += cardHeight + 12;
  });

  const filename = `${sanitizeFilename(baseFilename) || 'iniciativas-selecionadas'}.pdf`;
  doc.save(filename);
}
